import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { ACRESCIMOS_SUGERIDOS, DESCRICAO_DA_TAXA_DE_URGENCIA } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatBRL } from "./prosthesis-meta";
import { parsePriceToCents } from "./prosthesis-service-draft";
import type { AcrescimoDoPedidoRow } from "./types";

const inputCls =
  "h-8 rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";

/** Acréscimos do pedido (taxa de urgência, frete...). O laboratório lança e tira; a clínica só vê.
 *  Sem acréscimo e sem poder lançar, não aparece. */
export function AcrescimosDoPedido({
  orderId,
  acrescimos,
  podeEditar,
  taxaDeUrgenciaCents,
}: {
  orderId: string;
  acrescimos: AcrescimoDoPedidoRow[];
  podeEditar: boolean;
  /** Valor da taxa configurada pra este pedido: o atalho "Taxa de urgência" já vem com ele. */
  taxaDeUrgenciaCents?: number;
}) {
  const utils = trpc.useUtils();
  const [aberto, setAberto] = useState(false);
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  // Acréscimo muda o total do pedido, o recibo e o faturamento.
  const invalidar = () => {
    utils.prosthesis.invalidate();
    utils.financeiro.invalidate();
  };
  const adicionar = trpc.prosthesis.acrescimos.adicionar.useMutation({
    onSuccess: () => {
      invalidar();
      setDescricao("");
      setValor("");
      setAberto(false);
      toast.success("Acréscimo lançado.");
    },
    onError: (e) => toast.error(e.message || "Não foi possível lançar o acréscimo."),
  });
  const remover = trpc.prosthesis.acrescimos.remover.useMutation({
    onSuccess: invalidar,
    onError: (e) => toast.error(e.message || "Não foi possível tirar o acréscimo."),
  });

  if (acrescimos.length === 0 && !podeEditar) return null;
  const valorCents = parsePriceToCents(valor);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Acréscimos</p>
        {podeEditar && !aberto && (
          <button
            type="button"
            onClick={() => setAberto(true)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline dark:text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            Acréscimo
          </button>
        )}
      </div>

      {acrescimos.length > 0 && (
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 dark:divide-border dark:border-border">
          {acrescimos.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 flex-1 truncate text-zinc-800 dark:text-foreground">{a.descricao}</span>
              <span className="shrink-0 font-medium tabular-nums text-zinc-700 dark:text-foreground/80">{formatBRL(a.valorCents)}</span>
              {podeEditar && (
                <button
                  type="button"
                  onClick={() => window.confirm(`Tirar "${a.descricao}" do pedido?`) && remover.mutate({ id: a.id })}
                  aria-label={`Tirar ${a.descricao}`}
                  className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-red-600 dark:hover:bg-muted"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {acrescimos.length === 0 && !aberto && (
        <p className="text-xs text-zinc-500 dark:text-muted-foreground">Taxa de urgência, frete ou outro valor somado a este pedido.</p>
      )}

      {aberto && (
        <div className="mt-2 space-y-2 rounded-lg border border-zinc-200 p-3 dark:border-border">
          <div className="flex flex-wrap gap-1.5">
            {ACRESCIMOS_SUGERIDOS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setDescricao(s);
                  if (s === DESCRICAO_DA_TAXA_DE_URGENCIA && taxaDeUrgenciaCents && !valor.trim()) {
                    setValor((taxaDeUrgenciaCents / 100).toFixed(2).replace(".", ","));
                  }
                }}
                className={cn(
                  "rounded-md border px-2 py-1 text-xs font-medium",
                  descricao === s
                    ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                    : "border-zinc-200 text-zinc-600 hover:border-brand-300 dark:border-border dark:text-muted-foreground",
                )}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Descrição" aria-label="Descrição do acréscimo" className={cn(inputCls, "min-w-0 flex-1")} />
            <input value={valor} onChange={(e) => setValor(e.target.value)} placeholder="0,00" inputMode="decimal" aria-label="Valor do acréscimo" className={cn(inputCls, "w-28 text-right tabular-nums")} />
            <button
              type="button"
              disabled={descricao.trim().length < 2 || !valorCents || valorCents <= 0 || adicionar.isPending}
              onClick={() => adicionar.mutate({ orderId, descricao: descricao.trim(), valorCents: valorCents! })}
              className="inline-flex h-8 items-center gap-1 rounded-md bg-brand-600 px-3 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {adicionar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Lançar
            </button>
            <button type="button" onClick={() => setAberto(false)} className="h-8 rounded-md px-2 text-xs text-zinc-500 hover:bg-zinc-100 dark:hover:bg-muted">
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
